import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi, useAuth } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Search, ArrowRight } from 'lucide-react';
import EmptyState from '@/components/EmptyState';
import { RegistryLink } from '@/components/RegistryLink';
import RecordDocuments from '@/components/RecordDocuments';
import ClientRiskNotes from '@/components/ClientRiskNotes';
import AuditTrail from '@/components/AuditTrail';
import ConductWatch, { ConductChip, ConductFlagsDetail } from '@/components/ConductWatch';
import { useSort, SortableTh } from '@/components/SortableTh';
import SavedViews from '@/components/SavedViews';
import { Paperclip, History } from 'lucide-react';
import { StpBadge } from '@/components/StpBadge';

const DECISION_COLORS = {
  APPROVE: { bg: 'rgba(29,158,117,0.1)', text: '#1D9E75', border: '#1D9E75' },
  CONDITIONS: { bg: 'rgba(0,180,216,0.1)', text: '#00B4D8', border: '#00B4D8' },
  REFER: { bg: 'rgba(232,160,32,0.1)', text: '#E8A020', border: '#E8A020' },
  DECLINE: { bg: 'rgba(226,75,74,0.1)', text: '#E24B4A', border: '#E24B4A' },
};

const ROWS_PER_PAGE = 100;

const PIPELINE_STAGES = [
  { id: 'scored', label: 'Scored', color: '#00B4D8' },
  { id: 'conditions', label: 'Conditions', color: '#00B4D8' },
  { id: 'referred', label: 'Referred', color: '#E8A020' },
  { id: 'info_requested', label: 'With Broker', color: '#E8A020' },
  { id: 'referred_to_client', label: 'With Client', color: '#7C5CBF' },
  { id: 'deferred', label: 'Deferred', color: '#5C6B7A' },
  { id: 'expired', label: 'Expired', color: '#64748B' },
  { id: 'bound', label: 'Bound', color: '#1D9E75' },
  { id: 'lost', label: 'Lost', color: '#B0413E' },
  { id: 'declined', label: 'Declined', color: '#E24B4A' },
];

const EXPIRY_CHIP_STATUSES = ['scored', 'conditions', 'referred', 'info_requested', 'referred_to_client'];

function RowExpiryChip({ sub }) {
  if (!sub.quote_expires_at || !EXPIRY_CHIP_STATUSES.includes(sub.status)) return null;
  const days = Math.ceil((new Date(sub.quote_expires_at) - Date.now()) / 86400000);
  if (days > 7) return null;
  const color = days <= 2 ? '#E24B4A' : '#B27516';
  return (
    <span className="ml-1.5 px-1.5 py-0.5 rounded text-[9px] font-bold" style={{ background: color + '15', color }}
      data-testid={`submission-expiry-chip-${sub.submission_id}`}>
      {days < 0 ? 'EXPIRED' : days === 0 ? 'EXPIRES TODAY' : `${days}d LEFT`}
    </span>
  );
}

export default function SubmissionsPage() {
  const api = useApi();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isStaff = user?.role !== 'broker';
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [stageFilter, setStageFilter] = useState('all');
  const [loadError, setLoadError] = useState('');
  const [docsFor, setDocsFor] = useState(null);
  const [auditFor, setAuditFor] = useState(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    api.get('/submissions?limit=1000&view=list').then(r => setSubmissions(r.data)).catch(err => { console.error('API error:', err?.message); setLoadError('The pipeline could not be loaded — check the connection and try again.'); }).finally(() => setLoading(false));
  }, [api]);

  // Stage counts — 'Scored' is cumulative (every submission that received a score)
  const stageCounts = useMemo(() => {
    const counts = {};
    PIPELINE_STAGES.forEach(s => { counts[s.id] = 0; });
    submissions.forEach(s => { counts[s.status] = (counts[s.status] || 0) + 1; });
    counts.scored = submissions.filter(s =>
      s.composite_score != null || ['scored', 'conditions', 'referred', 'bound', 'declined'].includes(s.status)
    ).length;
    return counts;
  }, [submissions]);

  // Filter
  const filtered = useMemo(() => {
    let result = submissions;
    if (search) {
      const q = search.toLowerCase();
      const qDigits = q.replace(/\D/g, '');
      result = result.filter(s => s.business_name?.toLowerCase().includes(q)
        || s.broker_name?.toLowerCase().includes(q)
        || (qDigits && String(s.abn || '').replace(/\D/g, '').includes(qDigits)));
    }
    if (stageFilter !== 'all') {
      result = result.filter(s => s.status === stageFilter);
    }
    return result;
  }, [submissions, search, stageFilter]);

  const sort = useSort(filtered, 'created_at');
  // A thousand rows of table is seconds of layout on a phone — paint a page, offer the rest.
  const [visible, setVisible] = useState(ROWS_PER_PAGE);
  useEffect(() => { setVisible(ROWS_PER_PAGE); }, [search, stageFilter]);
  const rows = sort.sorted.slice(0, visible);

  if (loading) return <div className="space-y-3" data-testid="submissions-loading">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-14 rounded-md" />)}</div>;

  return (
    <div className="space-y-4" data-testid="submissions-page">
      {loadError && <p className="text-xs px-3 py-2 rounded-sm" style={{ background: 'rgba(226,75,74,0.08)', color: '#B03230' }} data-testid="submissions-load-error">{loadError}</p>}
      {/* Pipeline Funnel */}
      {submissions.length > 0 && (
        <div className="flex gap-1" data-testid="submission-pipeline">
          {PIPELINE_STAGES.map((stage, i) => {
            const count = stageCounts[stage.id] || 0;
            const isActive = stageFilter === stage.id;
            return (
              <button key={stage.id} onClick={() => setStageFilter(stageFilter === stage.id ? 'all' : stage.id)}
                className={`flex-1 relative py-3 px-3 rounded-md text-center transition-all ${isActive ? 'ring-2' : ''}`}
                style={{ background: stage.color + '08', ringColor: stage.color }}
                data-testid={`stage-${stage.id}`}>
                <p className="text-xl font-bold font-mono" style={{ color: stage.color }}>{count}</p>
                <p className="text-[10px] uppercase tracking-wider font-medium mt-0.5" style={{ color: '#5C6B7A' }}>{stage.label}</p>
                {i < PIPELINE_STAGES.length - 1 && (
                  <ArrowRight className="absolute right-[-8px] top-1/2 -translate-y-1/2 w-3 h-3 z-10" style={{ color: '#D0D5DD' }} />
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* Search */}
      <div className="flex gap-3 items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: '#5C6B7A' }} />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by business name, broker or ABN..."
            className="w-full pl-10 pr-4 py-2 text-sm rounded-md border outline-none focus:border-[#2A6FC4]"
            style={{ color: '#0D1B2A', borderColor: 'rgba(136,150,168,0.2)' }}
            data-testid="submission-search"
          />
        </div>
        <SavedViews page="submissions"
          getConfig={() => ({ search, stageFilter, sortKey: sort.sortKey, sortDir: sort.sortDir })}
          applyConfig={(c) => {
            setSearch(c.search || '');
            setStageFilter(c.stageFilter || 'all');
            sort.set(c.sortKey || 'created_at', c.sortDir || 'desc');
          }} />
      </div>

      {/* Submissions List */}
      {isStaff && <ConductWatch />}
      <Card className="rounded-md bg-white" style={{ border: '1px solid rgba(136,150,168,0.12)' }}>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="submissions-table">
            <thead>
              <tr className="border-b" style={{ background: '#FAFBFC', borderColor: 'rgba(136,150,168,0.1)' }}>
                <SortableTh label="Business" sortKey="business_name" sort={sort} />
                <SortableTh label="ABN" sortKey="abn" sort={sort} />
                <SortableTh label="Broker" sortKey="broker_name" sort={sort} />
                <SortableTh label="Score" sortKey="composite_score" sort={sort} />
                <SortableTh label="Decision" sortKey="decision" sort={sort} />
                <SortableTh label="Stage" sortKey="status" sort={sort} />
                <SortableTh label="STP" sortKey="stp" sort={sort} />
                <SortableTh label="Date" sortKey="created_at" sort={sort} />
                <th className="text-left py-2.5 px-4 text-[11px] uppercase tracking-wider font-medium" style={{ color: '#5C6B7A' }}>Docs</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={9} className="p-0">
                  {submissions.length === 0 ? <EmptyState type="submissions" /> : <EmptyState type="search" compact />}
                </td></tr>
              ) : rows.map((sub) => {
                const dc = DECISION_COLORS[sub.decision] || DECISION_COLORS.REFER;
                const stageConfig = PIPELINE_STAGES.find(s => s.id === sub.status);
                return (
                  <React.Fragment key={sub.submission_id}>
                  <tr data-testid={`submission-row-${sub.submission_id}`}
                    onClick={() => navigate(`/submissions/${sub.submission_id}`)}
                    title="Open quote details"
                    className="border-b hover:bg-[#FAFBFC] transition-colors cursor-pointer" style={{ borderColor: 'rgba(136,150,168,0.06)' }}>
                    <td className="py-2.5 px-4 text-xs font-medium" style={{ color: '#1A4C7C' }}>
                      {sub.business_name}
                      {isStaff && <ConductChip flags={sub.conduct_flags} />}
                    </td>
                    <td className="py-2.5 px-4 text-[11px] font-mono"><RegistryLink abn={sub.abn} nzbn={sub.nzbn} style={{ color: '#5C6B7A' }} /></td>
                    <td className="py-2.5 px-4 text-[11px]" style={{ color: '#5C6B7A' }}>{sub.broker_name === 'Simulation Engine' ? <span className="text-[9px] px-1.5 py-0.5 rounded font-bold tracking-wider" style={{ background: 'rgba(0,180,216,0.12)', color: '#0E7490' }}>SIM</span> : sub.broker_name}</td>
                    <td className="py-2.5 px-4">
                      <span className="font-mono text-xs font-semibold" style={{ color: dc.text }}>{sub.composite_score}</span>
                      <span className="text-[10px] ml-0.5" style={{ color: '#5C6B7A' }}>/10K</span>
                    </td>
                    <td className="py-2.5 px-4">
                      <span className="px-2 py-0.5 rounded text-[10px] font-semibold" style={{ background: dc.bg, color: dc.text }}>{sub.decision}</span>
                    </td>
                    <td className="py-2.5 px-4">
                      <span className="px-2 py-0.5 rounded text-[10px] font-medium capitalize" style={{ background: (stageConfig?.color || '#5C6B7A') + '12', color: stageConfig?.color || '#5C6B7A' }}>{stageConfig?.label || sub.status}</span>
                      {sub.hold && <span className="ml-1 px-1.5 py-0.5 rounded text-[9px] font-semibold tracking-wide" title={`${sub.hold.detail || ''}${sub.hold.correct ? ` — ${sub.hold.correct}` : ''}`} style={{ background: 'rgba(178,117,22,0.12)', color: '#B27516' }} data-testid={`submission-hold-chip-${sub.submission_id}`}>HELD · {sub.hold.reason === 'bind_gate' ? 'correction needed' : sub.hold.reason}</span>}
                      <RowExpiryChip sub={sub} />
                    </td>
                    <td className="py-2.5 px-4 text-[11px] font-medium" style={{ color: sub.stp ? '#00B4D8' : '#5C6B7A' }}>
                      {sub.stp ? <StpBadge compact /> : '—'}
                    </td>
                    <td className="py-2.5 px-4 text-[11px]" style={{ color: '#5C6B7A' }}>{new Date(sub.created_at).toLocaleDateString('en-AU')}</td>
                    <td className="py-2.5 px-4">
                      <div className="flex items-center gap-1">
                        <button onClick={(e) => { e.stopPropagation(); setDocsFor(docsFor === sub.submission_id ? null : sub.submission_id); }}
                          className="flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-medium transition-colors hover:opacity-80"
                          style={{ background: docsFor === sub.submission_id ? 'rgba(26,76,124,0.15)' : 'rgba(26,76,124,0.06)', color: '#1A4C7C' }}
                          data-testid={`submission-docs-btn-${sub.submission_id}`}>
                          <Paperclip className="w-3 h-3" />Docs
                        </button>
                        <button onClick={(e) => { e.stopPropagation(); setAuditFor(auditFor === sub.submission_id ? null : sub.submission_id); }}
                          className="flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-medium transition-colors hover:opacity-80"
                          style={{ background: auditFor === sub.submission_id ? 'rgba(232,160,32,0.18)' : 'rgba(232,160,32,0.08)', color: '#B27516' }}
                          data-testid={`submission-audit-btn-${sub.submission_id}`}>
                          <History className="w-3 h-3" />History
                        </button>
                      </div>
                    </td>
                  </tr>
                  {docsFor === sub.submission_id && (
                    <tr><td colSpan={9} className="px-6 py-3 space-y-3" style={{ background: '#FAFBFC' }}>
                      {sub.entity_id && <ClientRiskNotes entityId={sub.entity_id} editable={false} compact />}
                      <RecordDocuments recordType="submission" recordId={sub.submission_id} />
                    </td></tr>
                  )}
                  {auditFor === sub.submission_id && (
                    <tr><td colSpan={9} className="px-6 py-3" style={{ background: '#FAFBFC' }}>
                      {isStaff && <ConductFlagsDetail flags={sub.conduct_flags} />}
                      <AuditTrail submissionId={sub.submission_id} />
                    </td></tr>
                  )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
          {sort.sorted.length > visible && (
            <div className="flex items-center justify-center gap-3 py-3 border-t" style={{ borderColor: 'rgba(136,150,168,0.1)' }}>
              <span className="text-[11px]" style={{ color: '#5C6B7A' }} data-testid="submissions-showing-count">Showing {visible} of {sort.sorted.length}</span>
              <button type="button" onClick={() => setVisible(v => v + ROWS_PER_PAGE)} className="text-xs font-medium px-3 py-1.5 rounded-md hover:opacity-80" style={{ background: 'rgba(26,76,124,0.08)', color: '#1A4C7C' }} data-testid="submissions-show-more">
                Show {Math.min(ROWS_PER_PAGE, sort.sorted.length - visible)} more
              </button>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}
